import { faker } from '@faker-js/faker'
import { User } from '@sentry/react'
import { AxiosInstance } from 'axios'
import MockAdapter from 'axios-mock-adapter'
import { createCustomer } from './mocks/createCustomer'
import { generateBenchmarkingResponse } from './mocks/generateBenchmarkingResponse'
import { generateBillNotesResponse } from './mocks/generateBillNotesResponse'
import { generateInvoiceBillsResponse } from './mocks/generateInvoiceBillsResponse'
import { getInvoiceNos } from './mocks/generateInvoiceNoResponse'
import { generateInvoiceResponse } from './mocks/generateInvoiceResponse'
import { generatePayablesSummaryResponse } from './mocks/generatePayablesSummaryResponse'
import { generateReportResponse } from './mocks/generateReportResponse'
import { generateReportsResponse } from './mocks/generateReportsResponse'
import { getAccounts } from './mocks/getAccounts'
import { getCommodities } from './mocks/getCommodities'
import { getContracts } from './mocks/getContracts'
import { generateEnergyContractsResponse } from './mocks/generateEnergyContractsResponse'
import { getFacilities } from './mocks/getFacilities'
import { generateInvoicesSummaryResponse } from './mocks/getInvoicesSummaryResponse'
import { getMeters } from './mocks/getMeters'
import { getSubMeters } from './mocks/getSubMeters'
import { getNodes } from './mocks/getNodes'
import { getProviders } from './mocks/getProviders'
import { getTags } from './mocks/getTags'
import { generateLast12MonthsUsageCost } from './mocks/getUsagesAndCosts'
import { getUSNs } from './mocks/getUSNs'
import {
    Uuid,
    UserType,
    Customer,
    SaveReportRequestBody,
    GetReportResponse,
    GetRecentCustomersResponse,
    GetRolesAndPermissionsResponse,
} from './types'

import { isApiEndpointMocked } from './utils/isApiEndpointMocked'
import { mockDynamicEndpoint } from './utils/mockDynamicEndpoint'
import { GetSustainabilityTotalEmissionsResponseBody } from '@/hooks'
import { generateGHGEmissionsResponse } from './mocks/generateGHGEmissionsResponse'
import { generateAggregatedIntervalDataResponse } from './mocks/generateAggregatedIntervalDataResponse'

export const mockAxiosInstance = (axiosInstance: AxiosInstance) => {
    const mock = new MockAdapter(axiosInstance)

    const response: User = {
        id: faker.string.uuid() as Uuid,
        firstName: 'Sarah',
        lastName: 'Thompson',
        email: 'demo@contact.com',
        contactId: 123,
        type: UserType.CUSTOMER,
    }

    const mockCustomer: Customer = createCustomer({
        customerId: 1234,
        customerName: 'Company name',
        contactId: 5678,
    })

    const mockRecentCustomers: GetRecentCustomersResponse = [
        {
            recentCustomerId: '1',
            userId: '',
            customer: {
                customerId: 1,
                customerName: 'Customer 1',
            },
            accessedAt: '',
        },
        {
            recentCustomerId: '2',
            userId: '',
            customer: {
                customerId: 2,
                customerName: 'Customer 2',
            },
            accessedAt: '',
        },
        {
            recentCustomerId: '3',
            userId: '',
            customer: {
                customerId: 3,
                customerName: 'Customer 3',
            },
            accessedAt: '',
        },
    ]

    if (
        isApiEndpointMocked({
            method: 'delete',
            path: '/api/v1/sessions/:sessionId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/sessions/:sessionId',
                method: 'delete',
            },
            () => {
                return [204, null]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/sessions/:sessionId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/sessions/:sessionId',
                method: 'get',
            },
            () => {
                return [200, { sessionId: 123 }]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/users/:userId/customers',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/users/:userId/customers',
                method: 'get',
            },
            () => {
                return [200, [mockCustomer]]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/users/:userId/customers/recents',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/users/:userId/customers/recents',
                method: 'get',
            },
            () => {
                return [200, mockRecentCustomers]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/users/me',
        })
    ) {
        mock.onGet('/api/v1/users/me').reply(200, response)
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/reports/usage-cost',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/reports/usage-cost',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const usageCostResponse = generateLast12MonthsUsageCost()
                    resolve([200, usageCostResponse])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/contacts/:contactId/nodes',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/contacts/:contactId/nodes',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getNodes()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/commodities',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/commodities',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getCommodities()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/contracts',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/contracts',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getContracts()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/energy-contracts',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/energy-contracts',
            },
            (config) => {
                const limit = config.params
                    ? (config.params as { limit: number }).limit
                    : 20

                return new Promise<[number, unknown]>((resolve) => {
                    const response = generateEnergyContractsResponse(limit, 100)
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/pans',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/pans',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getAccounts()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/facilities',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/facilities',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getFacilities()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/providers',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/providers',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getProviders()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/users/:userId/tags',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/users/:userId/tags',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getTags()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/meters',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/meters',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getMeters()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/sub-meters',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/sub-meters',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getSubMeters()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/usns',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/usns',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getUSNs()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/invoices/numbers',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/invoices/numbers',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = getInvoiceNos()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/reports/payables-summary',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/reports/payables-summary',
            },
            (config) => {
                const isXlsxRequest =
                    config.headers?.Accept === 'application/vnd.ms-excel'

                if (isXlsxRequest) {
                    const dummyXlsxData = new Blob(['Dummy XLSX content'], {
                        type: 'application/vnd.ms-excel',
                    })
                    return [
                        200,
                        dummyXlsxData,
                        {
                            'Content-Disposition':
                                'attachment; filename="invoices.xlsx"',
                            'Content-Type': 'application/vnd.ms-excel',
                        },
                    ]
                }

                const limit = config.params
                    ? (config.params as { limit: number }).limit
                    : 50

                return new Promise<[number, unknown]>((resolve) => {
                    const payablesSummary = generatePayablesSummaryResponse(
                        limit,
                        400
                    )
                    resolve([200, payablesSummary])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/invoices/summary',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/invoices/summary',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = generateInvoicesSummaryResponse()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/invoices/:invoiceId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/invoices/:invoiceId',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    const response = generateInvoiceResponse()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/documents/:documentId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/documents/:documentId',
            },
            () => {
                const blob = new Blob([''], { type: 'application/pdf' })
                return [200, blob]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/documents',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/documents',
            },
            () => {
                const blob = new Blob([''], { type: 'application/zip' })
                return [200, blob]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/invoices/:invoiceId/bills',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/invoices/:invoiceId/bills',
            },

            (config) => {
                const limit = config.params
                    ? (config.params as { limit: number }).limit
                    : 50

                return new Promise<[number, unknown]>((resolve) => {
                    const invoiceBillsResponse = generateInvoiceBillsResponse(
                        limit,
                        400
                    )
                    resolve([200, invoiceBillsResponse])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/invoices/:invoiceId/bills/:billId/notes',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/invoices/:invoiceId/bills/:billId/notes',
            },

            (config) => {
                const limit = config.params
                    ? (config.params as { limit: number }).limit
                    : 50

                return new Promise<[number, unknown]>((resolve) => {
                    const billNotesResponse = generateBillNotesResponse(
                        limit,
                        400
                    )
                    resolve([200, billNotesResponse])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'post',
            path: '/api/v1/customers/:customerId/custom-reports',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'post',
                path: '/api/v1/customers/:customerId/custom-reports',
            },
            (config) => {
                return new Promise<[number, unknown]>((resolve) => {
                    const data = config.data
                        ? (JSON.parse(
                              config.data as string
                          ) as SaveReportRequestBody)
                        : null

                    if (!data?.name) {
                        resolve([500, { message: 'some other error' }])
                        return
                    }

                    if (data.name === 'test_duplicate') {
                        resolve([400, { message: 'already exists' }])
                        return
                    }

                    if (data.name === 'error') {
                        resolve([500, { message: 'Unknown error' }])
                        return
                    }

                    const response: GetReportResponse = {
                        customReportId: '6f791f26-38e3-44cb-9c31-ffea9484d540',
                        customerId: 456,
                        name: 'New report',
                        type: 'usage-cost',
                        owner: {
                            userId: 'user-123',
                            name: 'John Doe',
                        },
                        access: 'private',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        filters: {
                            nodeIds: [1],
                            commodityTypeIds: [1, 2],
                            startDate: '2024-01-01',
                            endDate: '2024-01-31',
                            providerIds: [10],
                            contractIds: [100],
                        },
                        fields: [
                            'date',
                            'usage_commodityUsage',
                            'cost_totalInvoiced',
                            'cost_commodity_totalCommodityCost',
                            'demand_actual',
                        ],
                    }
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/custom-reports/:reportId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/custom-reports/:reportId',
            },
            (req) => {
                return new Promise<[number, unknown]>((resolve) => {
                    let reportType: 'usage-cost' | 'payables-summary' =
                        'usage-cost'

                    const reportId = req.url?.split('/').pop() ?? ''
                    if (reportId.includes('payables')) {
                        reportType = 'payables-summary'
                    }

                    const response = generateReportResponse(
                        reportId,
                        reportType
                    )

                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/custom-reports',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/custom-reports',
            },
            (config) => {
                const limit = config.params
                    ? (config.params as { limit: number }).limit
                    : 50

                return new Promise<[number, unknown]>((resolve) => {
                    const response = generateReportsResponse(limit, 400)
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'delete',
            path: '/api/v1/customers/:customerId/custom-reports/:customReportId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'delete',
                path: '/api/v1/customers/:customerId/custom-reports/:customReportId',
            },
            () => {
                return new Promise<[number, unknown]>((resolve) => {
                    resolve([204, null])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'patch',
            path: '/api/v1/customers/:customerId/custom-reports/:customReportId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'patch',
                path: '/api/v1/customers/:customerId/custom-reports/:customReportId',
            },
            (config) => {
                return new Promise<[number, unknown]>((resolve) => {
                    const data = config.data
                        ? (JSON.parse(
                              config.data as string
                          ) as Partial<SaveReportRequestBody>)
                        : null

                    if (!data) {
                        resolve([400, { message: 'Invalid request data' }])
                        return
                    }

                    if (data.name === 'test_duplicate') {
                        resolve([
                            400,
                            { message: 'Report name already exists' },
                        ])
                        return
                    }

                    if (data.name === 'error') {
                        resolve([500, { message: 'Unknown error' }])
                        return
                    }

                    const urlParts = config.url?.split('/') || []
                    const customReportId = urlParts[urlParts.length - 1]

                    const response: GetReportResponse = {
                        customReportId: customReportId,
                        customerId: 456,
                        name: data.name || 'Updated Report',
                        type: data.type || 'usage-cost',
                        owner: {
                            userId: 'user-123',
                            name: 'John Doe',
                        },
                        access: 'private',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        filters: {
                            nodeIds: [243189],
                            commodityTypeIds: [1, 2],
                            startDate: '2024-01-01',
                            endDate: '2024-01-31',
                            providerIds: [10],
                            contractIds: [100],
                        },
                        fields: [
                            'date',
                            'usage_commodityUsage',
                            'cost_totalInvoiced',
                            'cost_commodity_totalCommodityCost',
                            'demand_actual',
                        ],
                    }

                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/reports/benchmarking',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/reports/benchmarking',
            },
            (config) => {
                const isXlsxRequest =
                    config.headers?.Accept === 'application/vnd.ms-excel'

                if (isXlsxRequest) {
                    const dummyXlsxData = new Blob(['Dummy XLSX content'], {
                        type: 'application/vnd.ms-excel',
                    })
                    return [
                        200,
                        dummyXlsxData,
                        {
                            'Content-Disposition':
                                'attachment; filename="benchmarking.xlsx"',
                            'Content-Type': 'application/vnd.ms-excel',
                        },
                    ]
                }

                return new Promise<[number, unknown]>((resolve) => {
                    const benchmarkingData = generateBenchmarkingResponse()
                    resolve([200, benchmarkingData])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'put',
            path: '/api/v1/sessions/:sessionId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'put',
                path: '/api/v1/sessions/:sessionId',
            },
            (config) => {
                const sessionId = config.url?.split('/').pop() ?? ''
                const response = {
                    sessionId,
                    accessToken: faker.string.uuid(),
                    refreshToken: faker.string.uuid(),
                    expiresIn: 3600,
                }
                return [200, response]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/reports/sustainability/total-emissions',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/customers/:customerId/reports/sustainability/total-emissions',
                method: 'get',
            },
            (config) => {
                const isXlsxRequest =
                    config.headers?.Accept === 'application/vnd.ms-excel'

                if (isXlsxRequest) {
                    const dummyXlsxData = new Blob(['Dummy XLSX content'], {
                        type: 'application/vnd.ms-excel',
                    })

                    return [
                        200,
                        dummyXlsxData,
                        {
                            'Content-Type': 'application/vnd.ms-excel',
                            'Content-Disposition':
                                'attachment; filename="total-emissions.xlsx"',
                        },
                    ]
                }

                // normal JSON response
                return new Promise<[number, unknown]>((resolve) => {
                    const response: GetSustainabilityTotalEmissionsResponseBody =
                        {
                            items: [
                                {
                                    name: 'Coal',
                                    percentage: 18.88,
                                    value: 18.88,
                                },
                                { name: 'Oil', percentage: 6.12, value: 6.12 },
                                {
                                    name: 'Natural Gas',
                                    percentage: 19.45,
                                    value: 19.45,
                                },
                                {
                                    name: 'Nuclear',
                                    percentage: 10.55,
                                    value: 10.55,
                                },
                                { name: 'Hydro', percentage: 5, value: 5 },
                                {
                                    name: 'Biomass',
                                    percentage: 5.5,
                                    value: 5.5,
                                },
                                { name: 'Wind', percentage: 30, value: 30 },
                                { name: 'Solar', percentage: 2.5, value: 2.5 },
                                { name: 'Fuel', percentage: 3, value: 3 },
                            ],
                            total: 100.92,
                        }

                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/users/me/roles-and-permissions',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/users/me/roles-and-permissions',
                method: 'get',
            },
            () => {
                const response: GetRolesAndPermissionsResponse[] = [
                    {
                        id: 'B6943123-6057-46A4-9938-0060AC12B57D',
                        name: 'Broker',
                        permissions: [
                            {
                                id: 'B6943698-6057-46A4-9938-0060AC12B5BB',
                                name: 'EXPORT DATA',
                            },
                            {
                                id: 'C7943598-6057-46A4-9936-0060AC12B5AC',
                                name: 'VIEW REPORT',
                            },
                        ],
                    },
                    {
                        id: '2DB5DBA4-040A4-4CB2-91B5-E5C991BEAF43',
                        name: 'Sustainability Full Access',
                        permissions: [
                            {
                                id: 'B6943698-6057-46A4-9938-0060AC12B5BB',
                                name: 'SUSTAINABILITY',
                            },
                            {
                                id: 'C7943598-6057-46A4-9936-0060AC12B5AC',
                                name: 'VIEW CO2E EMISSIONS',
                            },
                            {
                                id: 'D7943598-6057-46A4-9936-0060AC12B5AC',
                                name: 'MANAGE MARKET-BASED EMISSIONS',
                            },
                        ],
                    },
                ]

                return [200, response]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/users/me/roles-and-permissions',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/users/me/roles-and-permissions',
                method: 'get',
            },
            () => {
                const response: GetRolesAndPermissionsResponse[] = [
                    {
                        id: 'B6943123-6057-46A4-9938-0060AC12B57D',
                        name: 'Broker',
                        permissions: [
                            {
                                id: 'B6943698-6057-46A4-9938-0060AC12B5BB',
                                name: 'EXPORT DATA',
                            },
                            {
                                id: 'C7943598-6057-46A4-9936-0060AC12B5AC',
                                name: 'VIEW REPORT',
                            },
                        ],
                    },
                    {
                        id: '2DB5DBA4-040A4-4CB2-91B5-E5C991BEAF43',
                        name: 'Sustainability Full Access',
                        permissions: [
                            {
                                id: 'B6943698-6057-46A4-9938-0060AC12B5BB',
                                name: 'SUSTAINABILITY',
                            },
                            {
                                id: 'C7943598-6057-46A4-9936-0060AC12B5AC',
                                name: 'VIEW CO2E EMISSIONS',
                            },
                            {
                                id: 'D7943598-6057-46A4-9936-0060AC12B5AC',
                                name: 'MANAGE MARKET-BASED EMISSIONS',
                            },
                        ],
                    },
                ]

                return [200, response]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/reports/sustainability/ghg-emissions',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/customers/:customerId/reports/sustainability/ghg-emissions',
                method: 'get',
            },
            (config) => {
                const isXlsxRequest =
                    config.headers?.Accept === 'application/vnd.ms-excel'

                if (isXlsxRequest) {
                    const dummyXlsxData = new Blob(['Dummy XLSX content'], {
                        type: 'application/vnd.ms-excel',
                    })

                    return [
                        200,
                        dummyXlsxData,
                        {
                            'Content-Type': 'application/vnd.ms-excel',
                            'Content-Disposition':
                                'attachment; filename="ghg-emissions.xlsx"',
                        },
                    ]
                }
                const response = generateGHGEmissionsResponse()

                return [200, response]
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/interval-data',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                path: '/api/v1/customers/:customerId/interval-data',
                method: 'get',
            },
            (config) => {
                const isXlsxRequest =
                    config.headers?.Accept === 'application/vnd.ms-excel'

                if (isXlsxRequest) {
                    const dummyXlsxData = new Blob(['Dummy XLSX content'], {
                        type: 'application/vnd.ms-excel',
                    })

                    return [
                        200,
                        dummyXlsxData,
                        {
                            'Content-Type': 'application/vnd.ms-excel',
                            'Content-Disposition':
                                'attachment; filename="interval-data.xlsx"',
                        },
                    ]
                }

                return new Promise<[number, unknown]>((resolve) => {
                    const response = generateAggregatedIntervalDataResponse()
                    resolve([200, response])
                })
            }
        )
    }

    if (
        isApiEndpointMocked({
            method: 'get',
            path: '/api/v1/customers/:customerId/exports/:exportId',
        })
    ) {
        mockDynamicEndpoint(
            mock,
            {
                method: 'get',
                path: '/api/v1/customers/:customerId/exports/:exportId',
            },
            () => {
                return [
                    200,
                    {
                        downloadUrl:
                            'https://sample-videos.com/xls/Sample-Spreadsheet-100-rows.xls',
                    },
                ]
            }
        )
    }

    mock.onAny().passThrough()
}
